/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { type MessageDescriptor, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
// eslint-disable-next-line @atlaskit/editor/warn-no-restricted-imports
import ArchiveIcon from '@atlaskit/icon/core/archive-box';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { Box, Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';

import messages from './messages';

const styles = cssMap({
	archiveMessage: {
		borderRadius: token('radius.small'),
	},
	startNewChatPressable: {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		backgroundColor: 'transparent',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text'),
	},
});

type ErrorCodeConfig = {
	iconLabel: MessageDescriptor;
	message: MessageDescriptor;
	IconComponent: typeof ArchiveIcon | typeof LockLockedIcon;
};

const ERROR_CODE_CONFIG: Record<string, ErrorCodeConfig> = {
	[ASSISTANCE_SERVICE_ERROR_CODES.AGENT_FORBIDDEN]: {
		iconLabel: messages.noAgentPermissionIconLabel,
		message: messages.noAgentPermission,
		IconComponent: LockLockedIcon,
	},
	[ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED]: {
		iconLabel: messages.deletedAgentIconLabel,
		message: messages.deletedAgent,
		IconComponent: ArchiveIcon,
	},
} as const;

const DEFAULT_ERROR_CONFIG: ErrorCodeConfig = {
	iconLabel: messages.deletedAgentIconLabel,
	message: messages.deletedAgent,
	IconComponent: ArchiveIcon,
};

// TODO rename this to UnavailableAgentError with the file name
export const DeactivatedAgentError = ({
	handleNewChatClick,
	errorCode,
}: {
	handleNewChatClick: () => void;
	errorCode?: string;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const errorConfig = (errorCode && ERROR_CODE_CONFIG[errorCode]) || DEFAULT_ERROR_CONFIG;

	return (
		<Box padding="space.200">
			<Box
				paddingInline="space.150"
				paddingBlock="space.200"
				xcss={styles.archiveMessage}
				backgroundColor="elevation.surface.sunken"
			>
				<Flex gap="space.100" justifyContent="center" alignItems="center">
					<errorConfig.IconComponent
						label={formatMessage(errorConfig.iconLabel)}
						color={token('color.icon')}
					/>
					<Text weight="medium">{formatMessage(errorConfig.message)}</Text>
					<Pressable onClick={handleNewChatClick} xcss={styles.startNewChatPressable}>
						{formatMessage(messages.unavailableAgentStartNewChatButton)}
					</Pressable>
				</Flex>
			</Box>
		</Box>
	);
};
